import { noop } from 'underscore';
import React, { useCallback, useImperativeHandle, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CopilotSearchMMAEditor, ResponsiveAdRepository } from '@bingads-webui-campaign/responsive-ad-editor';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import config from '@bingads-webui-component-legacy/config';

import GenericImage from '../media/1.91to1.jpg';
import { getMMAStyles } from './mma-styles';

export const MmaEditor = ({
  ad,
  onAdChanged,
  i18n,
  perfMarker,
  permissions,
  scope,
  showAllValidationErrors,
  apiErrors,
  editorRef,
}) => {
  const internalEditorRef = React.createRef();
  const classes = useDefaultStyles(getMMAStyles);

  const legacyI18n = useMemo(() => mergeI18n(
    window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/audience-campaign'),
    i18n
  ), [i18n]);

  const viewContext = useMemo(() => ({
    currentCampaign: {
      Id: scope.campaignId,
      CampaignType: scope.campaignType,
    },
    currentAdGroup: {
      Id: scope.adGroupId,
    },
  }), [scope.campaignId, scope.campaignType, scope.adGroupId]);

  const responsiveAdRepository = useMemo(() => new ResponsiveAdRepository({
    viewContext,
    isVideo: false,
    campaignDomain: '',
  }), [viewContext]);

  const oDataConfig = useMemo(() => ({
    odataRootUrl: config.get('CampaignAzureDomain'),
    odataToken: config.get('odata.token'),
    odataPath: config.get('odata.url') + config.get('odata.path'),
  }), []);

  const onChanged = useCallback((data, isValid) => {
    onAdChanged({
      data,
      isValid,
    });
  }, [onAdChanged]);

  useImperativeHandle(editorRef, () => ({
    logAppliedSuggestions: (adData) => {
      if (internalEditorRef && internalEditorRef.current) {
        const {
          Id,
          FinalUrls,
          Headlines = {},
          LongHeadlines = {},
          Descriptions = {},
          ImageList,
          Images,
        } = adData;

        internalEditorRef.current.logAppliedSuggestion({
          assetData: {
            Id,
            FinalUrls,
            Headlines: Object.values(Headlines),
            LongHeadlines: Object.values(LongHeadlines),
            Descriptions: Object.values(Descriptions),
            Images: ImageList ? { ImageList } : Images,
          },
          isEditing: !!ad.data.Id,
        });
      }
    },
  }));

  return (
    <div className={classNames(classes.mma, 'mma-ad-editor-container')}>
      <I18nContext.Provider value={legacyI18n}>
        <CopilotSearchMMAEditor
          responsiveAdRepository={responsiveAdRepository}
          genericImage={GenericImage}
          viewContext={viewContext}
          ad={ad.data}
          onChanged={onChanged}
          perfMarker={perfMarker}
          oDataConfig={oDataConfig}
          permissions={permissions}
          hasWebsiteImageOptedInObservable={noop}
          editorRef={internalEditorRef}
          newI18n={i18n}
          externalErrors={apiErrors}
          showAllValidationErrors={showAllValidationErrors}
          showUnmappedFormErrors={false}
        />
      </I18nContext.Provider>
    </div>
  );
};

MmaEditor.propTypes = {
  ad: PropTypes.shape({
    data: PropTypes.shape({
      Id: PropTypes.string,
    }),
  }),
  onAdChanged: PropTypes.func.isRequired,
  i18n: PropTypes.shape({}).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    IsImpressionTrackingUrlsEnabled: PropTypes.bool,
  }).isRequired,
  scope: PropTypes.shape({
    accountId: PropTypes.string,
    customerId: PropTypes.string,
    userId: PropTypes.string,
    adGroupId: PropTypes.string,
    campaignId: PropTypes.string,
    campaignType: PropTypes.string,
  }).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  showAllValidationErrors: PropTypes.bool,
  apiErrors: PropTypes.arrayOf(PropTypes.shape({})),
  editorRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
};

MmaEditor.defaultProps = {
  ad: { data: {} },
  apiErrors: undefined,
  showAllValidationErrors: false,
};
